import { Plus, X, Info } from 'lucide-react';
import {
  type Operator,
  OPERATOR_LABELS,
  operatorsForType,
  needsValue,
  needsRangeValue,
} from './ruleOperators';

// ─── Types ───────────────────────────────────────────────────────────────────

export type QuestionType =
  | 'text'
  | 'number'
  | 'single_choice'
  | 'multiple_choice'
  | 'dropdown'
  | 'date'
  | 'photo'
  | 'file'
  // Employee Rating capability — constrained mobile pickers identified by type alone.
  | 'rating_1_5'
  // LEGACY. Superseded by tasks.product_mode='table'. Nothing authors one any
  // more; the member stays so unmigrated rows still typecheck.
  | 'product_grid'
  // The task's product picker. Present so the one shared vocabulary of question
  // types stays complete — but TasksPage filters it out of the rule list, since
  // its answer comes from a catalogue that changes independently of the task and
  // a rule written against it would quietly stop matching.
  | 'product_selection'
  | 'store_employee_picker';

export interface RuleQuestion {
  id: string;
  question_text: string;
  question_type: QuestionType;
  options: string[] | null;
  // Present when this question's own options are dynamic (depend on a parent
  // question's answer). Needed so downstream features (e.g. letting another
  // question depend on this one) can enumerate its possible answers from the
  // mapping instead of the empty top-level `options` array.
  option_source?: {
    depends_on_question_id: string;
    depends_on_question_ids?: string[];
    mapping: Record<string, { type: string; options?: string[] } | string[] | unknown>;
    // New fields for text/number parent support. Typed loosely here because
    // RuleQuestion is used only for read-side enumeration of possible answers
    // — the editor and validator use the stricter OptionSource type instead.
    patterns_by_parent?: Record<string, any[]>;
    default_by_parent?: Record<string, unknown>;
  } | null;
}

interface RuleCondition {
  question_id: string;
  operator: Operator;
  value?: any;
  // Only populated for operators that take a range (currently just `between`).
  value_to?: any;
}

export interface RuleGroup {
  match: 'all' | 'any';
  conditions: Array<RuleCondition | RuleGroup>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const isGroup = (node: any): node is RuleGroup => !!node && Array.isArray(node.conditions);

const operatorLabel = (op: Operator): string => OPERATOR_LABELS[op] ?? op;

// Build a readable one-line summary of a rule tree, used for the collapsed badge.
export function summarizeRule(
  rule: RuleGroup | null | undefined,
  questions: RuleQuestion[],
): string {
  if (!rule || !rule.conditions || rule.conditions.length === 0) return '';
  const conj = rule.match === 'any' ? ' OR ' : ' AND ';
  const parts = rule.conditions.map((c) => {
    if (isGroup(c)) return `(${summarizeRule(c, questions)})`;
    const q = questions.find((x) => x.id === c.question_id);
    const label = q ? q.question_text.slice(0, 24) : '?';
    const opLabel = operatorLabel(c.operator);
    if (!needsValue(c.operator)) return `${label} ${opLabel}`;
    if (needsRangeValue(c.operator)) return `${label} ${opLabel} ${c.value ?? ''}–${c.value_to ?? ''}`;
    return `${label} ${opLabel} "${c.value}"`;
  });
  return parts.join(conj);
}

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  rule: RuleGroup | null;
  onChange: (next: RuleGroup | null) => void;
  availableQuestions: RuleQuestion[];
  currentQuestionId?: string | null;
}

export default function RuleBuilder({ rule, onChange, availableQuestions, currentQuestionId }: Props) {
  const selectableQuestions = availableQuestions.filter((q) => q.id !== currentQuestionId);

  // When there is no rule yet, render a single "Add your first condition" prompt.
  if (!rule || rule.conditions.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4">
        <p className="text-xs text-gray-500 mb-3 flex items-start gap-1.5">
          <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-gray-400" />
          Add a condition and this question will only show when it matches. Leave empty to always show.
        </p>
        <button
          type="button"
          disabled={selectableQuestions.length === 0}
          onClick={() => {
            const firstQ = selectableQuestions[0];
            const ops = operatorsForType(firstQ?.question_type);
            onChange({
              match: 'all',
              conditions: [
                {
                  question_id: firstQ?.id || '',
                  operator: ops[0],
                  value: firstQ?.options?.[0] ?? '',
                },
              ],
            });
          }}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="h-3.5 w-3.5" /> Add first condition
        </button>
        {selectableQuestions.length === 0 && (
          <p className="mt-2 text-xs text-gray-400">Add at least one other question to this task first.</p>
        )}
      </div>
    );
  }

  return (
    <GroupEditor
      group={rule}
      onChange={(g) => onChange(g)}
      onDelete={() => onChange(null)}
      availableQuestions={selectableQuestions}
      depth={0}
    />
  );
}

// ─── GroupEditor (recursive) ─────────────────────────────────────────────────

interface GroupEditorProps {
  group: RuleGroup;
  onChange: (g: RuleGroup) => void;
  onDelete?: () => void;
  availableQuestions: RuleQuestion[];
  depth: number;
}

function GroupEditor({ group, onChange, onDelete, availableQuestions, depth }: GroupEditorProps) {
  const setMatch = (match: 'all' | 'any') => onChange({ ...group, match });
  const updateAt = (idx: number, next: RuleCondition | RuleGroup) => {
    const conditions = group.conditions.slice();
    conditions[idx] = next;
    onChange({ ...group, conditions });
  };
  const removeAt = (idx: number) => {
    const conditions = group.conditions.slice();
    conditions.splice(idx, 1);
    if (conditions.length === 0 && onDelete) {
      onDelete();
      return;
    }
    onChange({ ...group, conditions });
  };
  const addCondition = () => {
    const firstQ = availableQuestions[0];
    const ops = operatorsForType(firstQ?.question_type);
    onChange({
      ...group,
      conditions: [
        ...group.conditions,
        { question_id: firstQ?.id || '', operator: ops[0], value: firstQ?.options?.[0] ?? '' },
      ],
    });
  };
  const addGroup = () => {
    const firstQ = availableQuestions[0];
    const ops = operatorsForType(firstQ?.question_type);
    onChange({
      ...group,
      conditions: [
        ...group.conditions,
        {
          match: group.match === 'all' ? 'any' : 'all',
          conditions: [
            { question_id: firstQ?.id || '', operator: ops[0], value: firstQ?.options?.[0] ?? '' },
          ],
        },
      ],
    });
  };

  const containerBg = depth === 0 ? 'bg-white' : 'bg-gray-50';
  const accentBorder = depth === 0 ? 'border-gray-200' : 'border-primary-200';

  return (
    <div className={`rounded-lg border ${accentBorder} ${containerBg} p-3`}>
      <div className="flex items-center gap-2 mb-2.5">
        <span className="text-xs font-medium text-gray-600">When</span>
        <select
          value={group.match}
          onChange={(e) => setMatch(e.target.value as 'all' | 'any')}
          className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-semibold text-primary-700"
        >
          <option value="all">ALL</option>
          <option value="any">ANY</option>
        </select>
        <span className="text-xs text-gray-600">of the following are true:</span>
        {depth > 0 && onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="ml-auto p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 rounded"
            title="Remove group"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="space-y-2">
        {group.conditions.map((c, idx) =>
          isGroup(c) ? (
            <GroupEditor
              key={idx}
              group={c}
              onChange={(next) => updateAt(idx, next)}
              onDelete={() => removeAt(idx)}
              availableQuestions={availableQuestions}
              depth={depth + 1}
            />
          ) : (
            <ConditionRow
              key={idx}
              condition={c}
              onChange={(next) => updateAt(idx, next)}
              onDelete={() => removeAt(idx)}
              availableQuestions={availableQuestions}
            />
          )
        )}
      </div>

      <div className="mt-2.5 flex items-center gap-2">
        <button
          type="button"
          onClick={addCondition}
          className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
          <Plus className="h-3 w-3" /> Add condition
        </button>
        {depth === 0 && (
          <button
            type="button"
            onClick={addGroup}
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-primary-300 bg-white px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50"
          >
            <Plus className="h-3 w-3" /> Add {group.match === 'all' ? 'OR' : 'AND'} group
          </button>
        )}
      </div>
    </div>
  );
}

// ─── ConditionRow ────────────────────────────────────────────────────────────

interface ConditionRowProps {
  condition: RuleCondition;
  onChange: (c: RuleCondition) => void;
  onDelete: () => void;
  availableQuestions: RuleQuestion[];
}

function ConditionRow({ condition, onChange, onDelete, availableQuestions }: ConditionRowProps) {
  const q = availableQuestions.find((x) => x.id === condition.question_id);
  const operators = operatorsForType(q?.question_type);

  const setQuestion = (id: string) => {
    const nextQ = availableQuestions.find((x) => x.id === id);
    const nextOps = operatorsForType(nextQ?.question_type);
    onChange({
      question_id: id,
      operator: nextOps.includes(condition.operator) ? condition.operator : nextOps[0],
      value: nextQ?.options?.[0] ?? '',
    });
  };
  const setOp = (op: RuleCondition['operator']) => {
    const next: RuleCondition = {
      ...condition,
      operator: op,
      value: needsValue(op) ? condition.value ?? '' : undefined,
    };
    if (needsRangeValue(op)) next.value_to = condition.value_to ?? '';
    else delete next.value_to;
    onChange(next);
  };
  const setValue = (v: any) => onChange({ ...condition, value: v });
  const setValueTo = (v: any) => onChange({ ...condition, value_to: v });

  const isChoiceLike = q?.question_type === 'single_choice' || q?.question_type === 'dropdown' || q?.question_type === 'multiple_choice';

  return (
    <div className="flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1.5 hover:border-gray-300 hover:shadow-sm transition-all">
      <select
        value={condition.question_id}
        onChange={(e) => setQuestion(e.target.value)}
        className="flex-1 min-w-0 rounded px-1.5 py-1 text-xs text-gray-900 bg-transparent focus:outline-none focus:ring-1 focus:ring-primary-500 truncate"
      >
        {availableQuestions.length === 0 && <option value="">No other questions</option>}
        {availableQuestions.map((qq) => (
          <option key={qq.id} value={qq.id}>
            {qq.question_text.length > 40 ? qq.question_text.slice(0, 40) + '…' : qq.question_text}
          </option>
        ))}
      </select>

      <select
        value={condition.operator}
        onChange={(e) => setOp(e.target.value as RuleCondition['operator'])}
        className="flex-shrink-0 rounded px-1.5 py-1 text-xs text-gray-700 bg-transparent focus:outline-none focus:ring-1 focus:ring-primary-500"
      >
        {operators.map((op) => (
          <option key={op} value={op}>
            {operatorLabel(op)}
          </option>
        ))}
      </select>

      {needsValue(condition.operator) && (
        isChoiceLike && q?.options && q.options.length > 0 ? (
          <select
            value={condition.value ?? ''}
            onChange={(e) => setValue(e.target.value)}
            className="flex-shrink-0 min-w-[100px] max-w-[150px] rounded border border-gray-200 px-1.5 py-1 text-xs text-gray-900 bg-gray-50 focus:outline-none focus:ring-1 focus:ring-primary-500 truncate"
          >
            {q.options.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        ) : (
          <>
            <input
              type={q?.question_type === 'number' ? 'number' : 'text'}
              value={condition.value ?? ''}
              onChange={(e) => setValue(e.target.value)}
              placeholder={needsRangeValue(condition.operator) ? 'min' : 'value'}
              className="flex-shrink-0 w-24 rounded border border-gray-200 px-1.5 py-1 text-xs text-gray-900 bg-gray-50 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            {needsRangeValue(condition.operator) && (
              <>
                <span className="text-[11px] text-gray-500">to</span>
                <input
                  type={q?.question_type === 'number' ? 'number' : 'text'}
                  value={condition.value_to ?? ''}
                  onChange={(e) => setValueTo(e.target.value)}
                  placeholder="max"
                  className="flex-shrink-0 w-24 rounded border border-gray-200 px-1.5 py-1 text-xs text-gray-900 bg-gray-50 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </>
            )}
          </>
        )
      )}

      <button
        type="button"
        onClick={onDelete}
        className="flex-shrink-0 p-1 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
        title="Remove condition"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
